<script context="module">
  import { pagination } from "../../utilities/pagination";
</script>

<script>
  import { createEventDispatcher } from "svelte";
  import { Breakpoints, getBreakpoint } from "../../utilities/breakpoints";

  import Icon from "../../atoms/Icon.svelte";

  import PageSelector from "./PageSelector.svelte";
  import PageSelectorDropdown from "./PageSelectorDropdown.svelte";

  /** Primarily used for testing @type {string} */
  export let id = undefined;

  /**
   * @type {PaginationProps} props - The component props.
   */
  export let totalPages = 0;
  export let maxVisiblePagesDesktop = 10;
  export let maxVisiblePagesMobile = 4;
  export let initialActivePage = 1;

  $: currentPage = initialActivePage;

  let componentClass = "";
  export { componentClass as class };

  const classArrowFocus = `h-[3.121rem] focus-visible:outline-dashed focus-visible:outline-black focus-visible:outline-offset-0 focus-visible:outline-[2px]`;

  let ariaLiveRegionText;
  const updateAriaLive = () => {
    ariaLiveRegionText = `Current page is ${watchCurrentPage} out of ${totalPages}.`;
  };

  const ariaLabelContext = (defaultLabel, pageIdentifier) => {
    if (String(currentPage) == pageIdentifier) {
      return `${defaultLabel}, current page`;
    } else {
      return `${defaultLabel}`;
    }
  };

  const dispatch = createEventDispatcher();
  const navigateTo = (pageNumber) => {
    let page;
    if (pageNumber > 0 && pageNumber <= totalPages) {
      page = pageNumber;
    } else if (pageNumber <= 0) {
      page = totalPages;
    } else if (pageNumber > totalPages) {
      page = 1;
    }

    currentPage = page;
    dispatch("navigate", {
      page
    });
  };

  let screenWidth = 0;
  let visiblePagesOnDevice = 0;
  $: maxVisiblePages = setMaxVisiblePages(screenWidth);

  const setMaxVisiblePages = (screenWidth) => {
    if (getBreakpoint(screenWidth) === Breakpoints.sm) {
      visiblePagesOnDevice =
        maxVisiblePagesMobile < totalPages ? maxVisiblePagesMobile : totalPages;
    } else {
      visiblePagesOnDevice =
        maxVisiblePagesDesktop < totalPages ? maxVisiblePagesDesktop : totalPages;
    }

    return visiblePagesOnDevice;
  };

  $: watchCurrentPage = currentPage;
  $: ({ start, previousPages, visiblePages, nextPages, end } = pagination(
    totalPages,
    maxVisiblePages,
    watchCurrentPage
  ));
</script>

<svelte:window bind:innerWidth={screenWidth} />

{#if totalPages > 1}
  <nav {id} class="w-full border-y-thin border-black {componentClass}">
    <ul class="cta-md flex w-full list-none items-center justify-center gap-5 sm:gap-5 md:gap-5">
      <li class="list-none">
        <button
          on:click={() => {
            navigateTo(watchCurrentPage - 1);
            updateAriaLive();
          }}
          aria-label="Prev page"
          disabled={watchCurrentPage === 1}
          class="disabled:cursor-not-allowed disabled:text-grey {classArrowFocus}"
        >
          <Icon name="arrowLeft" />
        </button>
      </li>

      <li>
        <PageSelector
          pageNumber={start}
          current={start === watchCurrentPage}
          on:click={() => {
            navigateTo(start);
            updateAriaLive();
          }}
          arialLabel={ariaLabelContext("First page", start)}
        />
      </li>

      {#if previousPages.length > 0}
        <li>
          <PageSelectorDropdown
            pageNumbers={previousPages}
            onSelect={(pageNumber) => {
              navigateTo(pageNumber);
              updateAriaLive();
            }}
            arialLabel="Previous pages dropdown"
          />
        </li>
      {/if}

      {#each visiblePages as pageNumber}
        <li>
          <PageSelector
            {pageNumber}
            current={pageNumber === watchCurrentPage}
            on:click={() => {
              navigateTo(pageNumber);
              updateAriaLive();
            }}
            arialLabel={ariaLabelContext(`Page ${pageNumber}`, pageNumber)}
          />
        </li>
      {/each}

      {#if nextPages.length > 0}
        <li>
          <PageSelectorDropdown
            pageNumbers={nextPages}
            onSelect={(pageNumber) => {
              navigateTo(pageNumber);
              updateAriaLive();
            }}
            arialLabel="Next pages dropdown"
          />
        </li>
      {/if}

      {#if end > start}
        <li>
          <PageSelector
            pageNumber={end}
            current={end === watchCurrentPage}
            on:click={() => {
              navigateTo(end);
              updateAriaLive();
            }}
            arialLabel={ariaLabelContext("Last page", end)}
          />
        </li>
      {/if}

      <li>
        <button
          on:click={() => {
            navigateTo(watchCurrentPage + 1);
            updateAriaLive();
          }}
          aria-label="Next page"
          disabled={watchCurrentPage === totalPages}
          class="disabled:cursor-not-allowed disabled:text-grey {classArrowFocus}"
        >
          <Icon name="arrowRight" />
        </button>
      </li>
    </ul>
  </nav>
  <div aria-live="polite" class="sr-only">
    {ariaLiveRegionText}
  </div>
{/if}
